import { CountryIsoCode } from "@oysterhr/schema"
import { SalaryInsightsFormValues } from "./hooks/useSalaryInsightsForm"
import { Country } from "@oysterhr/feature-components"
import {
  countryCodeToCurrencyCode,
  countryCodeToName,
  nonPrioritizedCurrencies,
  prioritizedCurrencies,
} from "@oysterhr/common"
import {
  GlobalSalaryConverterFormValues,
  GlobalSalaryConverterSubmittedValues,
} from "./pages/GlobalSalaryConverterPage/components/GlobalSalaryConverterForm/types"
import { companyQuotePath, staffQuotePath } from "@oysterhr/cost-calculator"

export type SalaryInsightsQueryParams = {
  roleId: string
  roleName: string
  level: string
  country: string
  currency?: string
  amount?: string
}

export const buildSalaryInsightsSearchParams = (
  values: SalaryInsightsFormValues,
  searchParams?: URLSearchParams,
) => {
  const urlParams = searchParams ?? new URLSearchParams()

  urlParams.set("roleId", values.role.id)
  urlParams.set("roleName", values.role.name)
  urlParams.set("level", values.level)
  urlParams.set("country", values.country)
  urlParams.set("currency", values.currency)
  urlParams.set("amount", values.amount)

  return urlParams
}

export const getDataFromSearchParams = (
  searchParams?: URLSearchParams,
): SalaryInsightsFormValues => {
  const urlSearchParams = searchParams ?? new URL(window.location.href).searchParams
  const roleId = urlSearchParams.get("roleId")
  const roleName = urlSearchParams.get("roleName")
  const level = urlSearchParams.get("level")
  const country = urlSearchParams.get("country")
  const currency = urlSearchParams.get("currency")
  const amount = urlSearchParams.get("amount")

  const getValidCountry = (country: string) =>
    Object.values(CountryIsoCode).includes(country as CountryIsoCode)
      ? (country as CountryIsoCode)
      : ""

  return {
    role: {
      id: roleId ?? "",
      name: roleName ?? "",
    },
    level: level ?? "",
    country: getValidCountry(country ?? ""),
    currency: currency ?? "",
    amount: amount ?? "",
  }
}

export const getCurrencyForCountry = (countryCode: CountryIsoCode | undefined) => {
  return countryCodeToCurrencyCode(countryCode as CountryIsoCode) || "USD"
}

export const getCountriesListFromIsoCodes = (countryIsoCodes: CountryIsoCode[]) => {
  return (
    countryIsoCodes
      .map((code: string): Country => {
        return {
          code,
          name: countryCodeToName(code) || "",
        }
      })
      .sort((a, b) => a.name.localeCompare(b.name)) ?? []
  )
}

export const getCurrenciesOptions = () => {
  return [
    ...prioritizedCurrencies,
    ...nonPrioritizedCurrencies.sort((a, b) => a.currencyName.localeCompare(b.currencyName)),
  ]
}

export type GlobalSalaryConverterQueryParams = {
  from: string
  to: string
  amount: string
  currency: string
  destinationCurrency: string
  cpiRate?: string
  exchangeRate?: string
}

export const buildGlobalSalaryConverterSearchParams = (
  values: GlobalSalaryConverterFormValues,
  cpiRate?: number,
  exchangeRate?: number,
  searchParams?: URLSearchParams,
) => {
  const urlParams = searchParams ?? new URLSearchParams()

  urlParams.set("from", values.from)
  urlParams.set("to", values.to)
  urlParams.set("amount", values.salary.amount)
  urlParams.set("currency", values.salary.currency)
  urlParams.set("destinationCurrency", countryCodeToCurrencyCode(values.to))
  urlParams.set("cpiRate", cpiRate?.toString() || "")
  urlParams.set("exchangeRate", exchangeRate?.toString() || "")

  return urlParams
}

export const getGlobalSalaryConverterDataFromSearchParams = (
  searchParams?: URLSearchParams,
): GlobalSalaryConverterSubmittedValues => {
  const urlSearchParams = searchParams ?? new URL(window.location.href).searchParams
  const originCountryCode = urlSearchParams.get("from")
  const destinationCountryCode = urlSearchParams.get("to")
  const amount = urlSearchParams.get("amount")
  const currency = urlSearchParams.get("currency")
  const cpiRate = urlSearchParams.get("cpiRate")
  const exchangeRate = urlSearchParams.get("exchangeRate")

  const getValidCountry = (countryCode: string) =>
    Object.values(CountryIsoCode).includes(countryCode as CountryIsoCode)
      ? (countryCode as CountryIsoCode)
      : ""

  const destinationCurrency = urlSearchParams.get("destinationCurrency")

  return {
    from: getValidCountry(originCountryCode || "") ?? "",
    to: getValidCountry(destinationCountryCode || "") ?? "",
    salary: {
      amount: amount ?? "",
      currency: currency ?? "",
    },
    destinationCurrency:
      destinationCurrency || countryCodeToCurrencyCode(destinationCountryCode ?? "") || "",
    cpiRate: cpiRate ? +cpiRate : 0,
    exchangeRate: exchangeRate ? +exchangeRate : 1,
  }
}

export function buildCostCalculatorLink({
  countryCode,
  currencyCode,
  annualGrossSalary,
  vatServices,
  isStaff,
}: {
  countryCode: CountryIsoCode
  annualGrossSalary: number | null
  currencyCode: string
  vatServices?: boolean
  isStaff?: boolean
}) {
  const urlParams = new URLSearchParams({
    countryCode,
    annualGrossSalary: annualGrossSalary ? annualGrossSalary.toString() : "",
    currencyCode,
    totalCurrency: currencyCode,
    vatServices: vatServices ? "true" : "false",
  })

  if (isStaff) {
    return staffQuotePath(urlParams)
  }

  return companyQuotePath("", urlParams)
}
